import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../rootReducer';
import { checkEmail, createUsersInBulk } from './index.thunks';

export enum EmailStatusType {
  Valid = 'Valid',
  Invalid = 'Invalid',
  AlreadyExists = 'AlreadyExists',
  AlreadyAddedToList = 'AlreadyAddedToList',
}

export interface IUserBulkCreationState {
  email: string;
  checkEmailLoading: boolean;
  emailStatus: EmailStatusType | null;
  loading: boolean;
  errors: UserErrorCode[];
  emailsWithErrors: string[];
  message: string;
}

export const initialState: IUserBulkCreationState = {
  email: '',
  checkEmailLoading: false,
  emailStatus: null,
  loading: false,
  errors: [],
  emailsWithErrors: [],
  message: ''
};

export interface ICheckEmailResponse {
  email: string;
  hasUser: boolean;
}

export type ICreateUsersInBulkResponse = {
  message: string;
  errors: { [error in UserErrorCode]?: string[]; };
};

export enum UserErrorCode {
  InvalidFirstName = 'InvalidFirstName',
  InvalidLastName = 'InvalidLastName',
  InvalidEmail = 'InvalidEmail',
  EmailAlreadyExists = 'EmailAlreadyExists',
}

const getEmail = (state: RootState): string => state.users.bulkCreation.email;
const getCheckEmailLoading = (state: RootState): boolean => state.users.bulkCreation.checkEmailLoading;
const getEmailStatus = (state: RootState): EmailStatusType | null => state.users.bulkCreation.emailStatus;
const getLoading = (state: RootState): boolean => state.users.bulkCreation.loading;
const getErrors = (state: RootState): UserErrorCode[] => state.users.bulkCreation.errors;
const getMessage = (state: RootState): string => state.users.bulkCreation.message;
const getEmailsWithErrors = (state: RootState): string[] => state.users.bulkCreation.emailsWithErrors;

export const selectors = {
  getCheckEmailLoading,
  getEmail,
  getEmailStatus,
  getLoading,
  getErrors,
  getMessage,
  getEmailsWithErrors
};

const bulkCreationSlice = createSlice({
  name: 'bulkCreation',
  initialState,
  reducers: {
    clearValues: (state) => {
      state.email = '';
      state.checkEmailLoading = false;
      state.emailStatus = null;
      state.errors = [];
      state.emailsWithErrors = [];
      state.message = '';
    },
    clearCheckEmailValues: (state) => {
      state.email = '';
      state.checkEmailLoading = false;
      state.emailStatus = null;
    },
    setEmailIsAlreadyAddedToList: (state) => {
      state.email = '';
      state.checkEmailLoading = false;
      state.emailStatus = EmailStatusType.AlreadyAddedToList;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkEmail.pending, (state) => {
        state.checkEmailLoading = true;
        state.emailStatus = null;
      })
      .addCase(checkEmail.fulfilled, (state, action) => {
        const { email, hasUser } = action.payload;
        state.checkEmailLoading = false;
        state.email = email;
        state.emailStatus = hasUser ? EmailStatusType.AlreadyExists : EmailStatusType.Valid;
      })
      .addCase(checkEmail.rejected, (state) => {
        state.checkEmailLoading = false;
        state.emailStatus = EmailStatusType.Invalid;
      })
      .addCase(createUsersInBulk.pending, (state) => {
        state.loading = true;
        state.errors = [];
        state.emailsWithErrors = [];
      })
      .addCase(createUsersInBulk.fulfilled, (state) => {
        state.loading = false;
        state.errors = [];
        state.emailsWithErrors = [];
      })
      .addCase(createUsersInBulk.rejected, (state, action) => {
        const { errors, message } = action.payload as ICreateUsersInBulkResponse;
        const emails = Object.values(errors).flat().filter((e): e is string => e !== undefined);
        const emailsWithErrors = Array.from(new Set(emails));
        state.loading = false;
        state.emailStatus = EmailStatusType.Invalid;
        state.message = message;
        state.errors = Object.keys(errors)
          .map(key => UserErrorCode[key as keyof typeof UserErrorCode]);
        state.emailsWithErrors = emailsWithErrors;
      });
  }
});

export const bulkCreationReducer = bulkCreationSlice.reducer;

export const actions = bulkCreationSlice.actions;
export const caseReducers = bulkCreationSlice.caseReducers;
